import React, { useState } from "react";
import { Button, Dropdown } from "antd";
import { CheckOutlined, DownOutlined, LogoutOutlined, MenuOutlined, QuestionCircleOutlined, UserOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import Alerts from "/src/util/alert";

import { DEFAULT_LANG, LANGS } from "../config/languages";
import AppNotifications from "./AppNotifications";
import AppSearch from "./AppSearch";
import { MANAGER_PROFILE, PROFILE_PAGE, SIDE_NAV } from "../config/lang";
import { call } from "../util/action";
import { logout, update_me } from "../util/redux/actions";
import { translate } from "../util/util";

function initialsOf(me) {
  const a = me?.first_name?.[0] || "";
  const b = me?.last_name?.[0] || "";
  return (a + b).toUpperCase() || "?";
}

function Avatar({ me, onError, imgFailed }) {
  const fullName = `${me?.first_name || ""} ${me?.last_name || ""}`.trim();
  if (me?.photo && !imgFailed) {
    return <img className="shell-avatar shell-avatar--img" src={me.photo} alt={fullName} onError={onError} />;
  }
  return (
    <span className="shell-avatar" aria-hidden="true">
      {initialsOf(me)}
    </span>
  );
}

/** The language lives on the user record, so switching it is a write, not local state. */
function LanguageMenu({ me, lang }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);

  const change = code => {
    if (code === lang || saving || !me?.id) return;
    setSaving(true);
    /* PATCH, not PUT: the profile page PUTs the whole record, and sending a partial record
       there would clobber fields. One key is all that changes here. */
    call({ method: "PATCH", url: "users/" + me.id + "/", post: { language: code } }, r => {
      setSaving(false);
      if (!r || !r.id) {
        Alerts.warning("Could not change the language. " + JSON.stringify(r));
        return;
      }
      dispatch(update_me({ ...me, ...r }));
      /* Most of the app reads me.language at render time from module-scope state, so a reload
         is still how a language change reaches every screen. Same as the profile page. */
      setTimeout(() => window.location.reload(), 150);
    });
  };

  const items = LANGS.map(l => ({
    key: l.code,
    label: (
      <span className="shell-langitem">
        <span>{l.label}</span>
        {l.native ? <span className="shell-langitem__native">{l.native}</span> : null}
        {l.code === lang ? <CheckOutlined className="shell-langitem__tick" /> : null}
      </span>
    ),
  }));

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      classNames={{ root: "shell-langmenu" }}
      autoFocus /* see the user menu below — the rows are unreachable by keyboard without it */
      menu={{ items, selectable: true, selectedKeys: [lang], onClick: ({ key }) => change(key) }}>
      {/* A two-letter code beats any glyph here: it says which language is active without
          being opened, which no icon can do. */}
      <Button className="shell-lang" aria-label={translate(MANAGER_PROFILE, "language", lang) || "Language"}>
        <span className="shell-lang__code">{(lang || DEFAULT_LANG).toUpperCase()}</span>
        <DownOutlined className="shell-lang__chev" />
      </Button>
    </Dropdown>
  );
}

const ROLE_KEY = {
  supervisor_manager: "role_supervisor_manager",
  supervisor: "role_supervisor",
};

function UserMenu({ me, lang }) {
  const dispatch = useDispatch();
  const [imgFailed, setImgFailed] = useState(false);
  const fullName = `${me?.first_name || ""} ${me?.last_name || ""}`.trim();
  /* The design shows the ROLE under the name, not the free-text position — and `position` is
     empty on most accounts, so the line simply vanished. */
  const roleLabel = ROLE_KEY[me?.role] ? translate(PROFILE_PAGE, ROLE_KEY[me.role], lang) : me?.role || "";

  const items = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: <Link to="/profile">{translate(MANAGER_PROFILE, "my_profile", lang)}</Link>,
    },
    {
      key: "help",
      icon: <QuestionCircleOutlined />,
      label: <Link to="/help">{translate(MANAGER_PROFILE, "my_help", lang)}</Link>,
    },
    { type: "divider" },
    {
      key: "logout",
      danger: true,
      icon: <LogoutOutlined />,
      label: translate(MANAGER_PROFILE, "exit", lang) || translate(SIDE_NAV, "logout", lang),
      onClick: () => dispatch(logout()),
    },
  ];

  /* The identity block is our own markup above the menu, not a menu group. As a `type: "group"`
     it inherited antd's group-title and group-list padding, whose selectors carry more classes
     than any override we could sanely write — the design's "identity block, then a bordered list
     of rows" came out with the rows flush to the panel edges. `popupRender` hands us the menu
     node to wrap, so antd keeps the roving-focus keyboard handling and we keep the layout.
     Same shape AppNotifications already uses. */
  const panel = menu => (
    <div
      className="shell-usermenu__panel"
      /* rc-dropdown's autoFocus calls `.focus()` on whatever popupRender returns. A plain div
         swallows that — and since keydown bubbles up rather than down, focus parked on the
         wrapper never reaches the menu's own arrow-key handling. So take the focus and hand it
         straight on to the list. */
      tabIndex={-1}
      onFocus={e => {
        if (e.target === e.currentTarget) e.currentTarget.querySelector(".ant-dropdown-menu")?.focus();
      }}>
      <div className="shell-usermenu__id">
        <Avatar me={me} imgFailed={imgFailed} onError={() => setImgFailed(true)} />
        <span>
          <span className="shell-usermenu__nm">{fullName}</span>
          {roleLabel ? <span className="shell-usermenu__rl">{roleLabel}</span> : null}
        </span>
      </div>
      {menu}
    </div>
  );

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      classNames={{ root: "shell-usermenu" }}
      popupRender={panel}
      /* Without it the menu opens but focus stays on the trigger: ↓ does nothing and Tab walks
         past the rows to the next bar control, so the menu is mouse-only. */
      autoFocus
      menu={{ items }}>
      <button className="shell-userchip" type="button">
        <Avatar me={me} imgFailed={imgFailed} onError={() => setImgFailed(true)} />
        <span className="shell-userchip__meta">
          <span className="shell-userchip__name">{fullName}</span>
          {roleLabel ? <span className="shell-userchip__role">{roleLabel}</span> : null}
        </span>
        <DownOutlined className="shell-userchip__chev" />
      </button>
    </Dropdown>
  );
}

/**
 * Sticky app bar. Owns `top: 0` / `z-index: 30`; every other sticky strip on a page must sit
 * below it on both axes — see docs/design/spec/03-patterns.md#sticky-layering.
 *
 * Order is burger, search, spacer, notifications, language, user. `.shell-bar__spacer` is what
 * splits the two clusters: search stays left, everything that acts on the account stays right.
 */
export default function AppBar({ onOpenNav }) {
  const me = useSelector(state => state.me);
  const lang = me?.language;

  return (
    <header className="shell-bar">
      <button className="shell-iconbtn shell-burger" type="button" onClick={onOpenNav} aria-label="Open navigation">
        <MenuOutlined />
      </button>

      <AppSearch />

      <span className="shell-bar__spacer" />

      <AppNotifications />
      <LanguageMenu me={me} lang={lang} />
      <UserMenu me={me} lang={lang} />
    </header>
  );
}
